import { useState, useEffect, useCallback, useRef } from 'react';
import { Save, Download, Upload, Calendar, Trash2, RefreshCw, AlertTriangle, CheckCircle, XCircle, Clock, Loader2 } from 'lucide-react';
import { systemService } from '../../../services';
import type { BackupData, BackupSettings, BackupJob } from '../../../services';
import ConfirmDialog from '../../../components/common/ConfirmDialog';
import { useToast } from '../../../components/common/ToastContainer';
import { formatDate } from '../../../utils/formatDate';
import { useSettingsStore } from '../../../stores/settingsStore';
import { usePermissions } from '../../../hooks/usePermissions';

interface BackupRestoreTabProps {
  data: BackupData | null;
  onUpdate: (data: BackupData) => void;
}

/** Format bytes to human-readable size */
function formatBytes(bytes: number): string {
  if (bytes === 0) return '0 B';
  const k = 1024;
  const sizes = ['B', 'KB', 'MB', 'GB'];
  const i = Math.floor(Math.log(bytes) / Math.log(k));
  return `${parseFloat((bytes / Math.pow(k, i)).toFixed(1))} ${sizes[i]}`;
}

/** Status badge colors */
function getStatusColor(status: string): string {
  switch (status) {
    case 'completed': return 'bg-green-100 text-green-800';
    case 'failed': return 'bg-red-100 text-red-800';
    case 'in_progress': case 'restoring': return 'bg-blue-100 text-blue-800';
    case 'pending': return 'bg-yellow-100 text-yellow-800';
    case 'dry_run_complete': return 'bg-purple-100 text-purple-800';
    default: return 'bg-gray-100 text-gray-800';
  }
}

function getStatusLabel(status: string): string {
  switch (status) {
    case 'completed': return 'Completed';
    case 'failed': return 'Failed';
    case 'in_progress': return 'In Progress';
    case 'restoring': return 'Restoring';
    case 'pending': return 'Pending';
    case 'dry_run_complete': return 'Ready to Restore';
    default: return status;
  }
}

function getStatusIcon(status: string) {
  switch (status) {
    case 'completed': return <CheckCircle className="w-4 h-4" />;
    case 'failed': return <XCircle className="w-4 h-4" />;
    case 'in_progress': case 'restoring': case 'pending': return <Loader2 className="w-4 h-4 animate-spin" />;
    case 'dry_run_complete': return <AlertTriangle className="w-4 h-4" />;
    default: return <Clock className="w-4 h-4" />;
  }
}

const BackupRestoreTab = ({ data, onUpdate }: BackupRestoreTabProps) => {
  const toast = useToast();
  const { canCreate, canUpdate, canDelete } = usePermissions();
  const { getDateFormatSettings } = useSettingsStore();
  const dateSettings = getDateFormatSettings();
  const [saving, setSaving] = useState(false);
  const [creating, setCreating] = useState(false);
  const [formData, setFormData] = useState<BackupSettings | null>(null);
  const [deleteConfirm, setDeleteConfirm] = useState<{ isOpen: boolean; backupId: string | null }>({ isOpen: false, backupId: null });

  // Restore flow state
  const [restoreJobId, setRestoreJobId] = useState<string | null>(null);
  const [restoreJob, setRestoreJob] = useState<BackupJob | null>(null);
  const [showDryRunModal, setShowDryRunModal] = useState(false);
  const [confirmText, setConfirmText] = useState('');
  const [confirming, setConfirming] = useState(false);

  // Polling
  const pollRef = useRef<ReturnType<typeof setInterval> | null>(null);

  useEffect(() => {
    if (data) {
      setFormData(data.settings);
    }
  }, [data]);

  // Poll for active jobs
  const hasActiveJobs = data?.recentBackups.some(
    b => b.status === 'pending' || b.status === 'in_progress' || b.status === 'restoring'
  );

  useEffect(() => {
    // Clear any existing interval first
    if (pollRef.current) {
      clearInterval(pollRef.current);
      pollRef.current = null;
    }

    if (hasActiveJobs) {
      pollRef.current = setInterval(async () => {
        try {
          const updated = await systemService.getBackup();
          onUpdate(updated);
        } catch { /* ignore */ }
      }, 5000);
    }

    return () => {
      if (pollRef.current) {
        clearInterval(pollRef.current);
        pollRef.current = null;
      }
    };
  }, [hasActiveJobs, onUpdate]);

  // Poll restore job status
  useEffect(() => {
    if (!restoreJobId) return;
    const interval = setInterval(async () => {
      try {
        const job = await systemService.getBackupJob(restoreJobId);
        setRestoreJob(job);
        if (job.status === 'dry_run_complete') {
          setShowDryRunModal(true);
          clearInterval(interval);
        } else if (job.status === 'completed' || job.status === 'failed') {
          clearInterval(interval);
          setRestoreJobId(null);
          if (job.status === 'completed') {
            toast.success('Restore Complete', 'Database has been restored successfully');
          } else {
            toast.error('Restore Failed', job.errorMessage || 'Unknown error');
          }
          // Refresh
          const updated = await systemService.getBackup();
          onUpdate(updated);
        }
      } catch { /* ignore */ }
    }, 2000);

    return () => clearInterval(interval);
  }, [restoreJobId, onUpdate, toast]);

  const handleSave = async () => {
    if (!formData) return;
    try {
      setSaving(true);
      const updated = await systemService.updateBackup(formData);
      onUpdate(updated);
      toast.success('Settings Updated', 'Backup settings updated successfully');
    } catch (err: any) {
      toast.error('Update Failed', `Failed to update backup settings: ${err.message}`);
    } finally {
      setSaving(false);
    }
  };

  const handleCreateBackup = async () => {
    try {
      setCreating(true);
      await systemService.createBackup();
      toast.success('Backup Started', 'Backup is being created...');
      // Refresh to show new pending job
      const updated = await systemService.getBackup();
      onUpdate(updated);
    } catch (err: any) {
      toast.error('Backup Failed', `Failed to create backup: ${err.message}`);
    } finally {
      setCreating(false);
    }
  };

  const handleRestore = async (backupId: string) => {
    try {
      const result = await systemService.restoreBackup(backupId);
      setRestoreJobId(result.jobId);
      setRestoreJob(null);
      setConfirmText('');
      toast.success('Integrity Check', 'Running integrity check on backup...');
    } catch (err: any) {
      toast.error('Restore Failed', `Failed to initiate restore: ${err.message}`);
    }
  };

  const handleConfirmRestore = async () => {
    if (!restoreJobId || confirmText !== 'RESTORE') return;
    try {
      setConfirming(true);
      await systemService.confirmRestore(restoreJobId);
      setShowDryRunModal(false);
      toast.success('Restore Started', 'Database restore is in progress...');
      // Keep polling via restoreJobId
    } catch (err: any) {
      toast.error('Restore Failed', `Failed to confirm restore: ${err.message}`);
    } finally {
      setConfirming(false);
    }
  };

  const handleDelete = async () => {
    if (!deleteConfirm.backupId) return;
    try {
      await systemService.deleteBackup(deleteConfirm.backupId);
      toast.success('Deleted', 'Backup deleted successfully');
      setDeleteConfirm({ isOpen: false, backupId: null });
      const updated = await systemService.getBackup();
      onUpdate(updated);
    } catch (err: any) {
      toast.error('Delete Failed', `Failed to delete backup: ${err.message}`);
    }
  };

  const handleDownload = async (backupId: string) => {
    try {
      await systemService.downloadBackup(backupId);
    } catch (err: any) {
      toast.error('Download Failed', `Failed to download backup: ${err.message}`);
    }
  };

  const handleRefresh = useCallback(async () => {
    try {
      const updated = await systemService.getBackup();
      onUpdate(updated);
    } catch { /* ignore */ }
  }, [onUpdate]);

  if (!data || !formData) {
    return <div className="text-gray-500">Loading...</div>;
  }

  const backupJobs = data.recentBackups.filter(j => j.type === 'backup');
  const restoreJobs = data.recentBackups.filter(j => j.type === 'restore');

  return (
    <div className="space-y-6">
      {/* Backup Settings */}
      <div>
        <h2 className="text-lg font-semibold text-gray-900 mb-4">Backup Settings</h2>
        <div className="space-y-4">
          <ToggleField
            label="Enable Automatic Backups"
            checked={formData.enabled}
            onChange={(checked) => setFormData({ ...formData, enabled: checked })}
          />
          {formData.enabled && (
            <>
              <FormField
                label="Schedule"
                value={formData.schedule}
                onChange={(e) => setFormData({ ...formData, schedule: e.target.value })}
                type="select"
                options={['daily', 'weekly', 'monthly']}
              />
              <div>
                <FormField
                  label="Schedule Time"
                  value={formData.scheduleTime}
                  onChange={(e) => setFormData({ ...formData, scheduleTime: e.target.value })}
                  type="time"
                />
                <p className="text-xs text-gray-400 mt-1">
                  Timezone: {dateSettings.timeZone}
                </p>
              </div>
              <FormField
                label="Retention (days)"
                value={formData.retentionDays.toString()}
                onChange={(e) => setFormData({ ...formData, retentionDays: parseInt(e.target.value) || 30 })}
                type="number"
              />
            </>
          )}
        </div>
      </div>

      {/* Manual Backup */}
      <div>
        <div className="flex items-center justify-between mb-4">
          <h2 className="text-lg font-semibold text-gray-900">Manual Backup</h2>
          <button
            onClick={handleRefresh}
            className="btn-secondary text-sm inline-flex items-center space-x-1"
          >
            <RefreshCw className="w-3 h-3" />
            <span>Refresh</span>
          </button>
        </div>
        <div className="p-4 bg-gray-50 rounded-lg">
          <p className="text-sm text-gray-600 mb-4">
            Create a full database backup. Includes all tables, stored procedures, triggers, and system settings.
          </p>
          {canCreate && (
          <button
            onClick={handleCreateBackup}
            disabled={creating}
            className="btn-primary inline-flex items-center space-x-2 disabled:opacity-50"
          >
            {creating ? <Loader2 className="w-4 h-4 animate-spin" /> : <Download className="w-4 h-4" />}
            <span>{creating ? 'Creating...' : 'Create Backup Now'}</span>
          </button>
          )}
        </div>
      </div>

      {/* Recent Backups */}
      <div>
        <h2 className="text-lg font-semibold text-gray-900 mb-4">Backups</h2>
        {backupJobs.length > 0 ? (
          <div className="space-y-2">
            {backupJobs.map((job) => (
              <div key={job.id} className="flex items-center justify-between p-4 bg-gray-50 rounded-lg">
                <div className="flex items-center space-x-4">
                  <Calendar className="w-5 h-5 text-gray-400" />
                  <div>
                    <div className="text-sm font-medium text-gray-900">
                      {job.createdAt ? formatDate(new Date(job.createdAt), dateSettings) : 'Unknown'}
                    </div>
                    <div className="text-xs text-gray-500">
                      {job.fileName || 'Creating...'} {job.fileSize > 0 && `(${formatBytes(job.fileSize)})`}
                    </div>
                    {job.errorMessage && (
                      <div className="text-xs text-red-600 mt-1">{job.errorMessage}</div>
                    )}
                  </div>
                  <span className={`inline-flex items-center space-x-1 px-2.5 py-0.5 rounded-full text-xs font-medium ${getStatusColor(job.status)}`}>
                    {getStatusIcon(job.status)}
                    <span>{getStatusLabel(job.status)}</span>
                  </span>
                </div>
                <div className="flex items-center space-x-2">
                  {job.status === 'completed' && (
                    <>
                      <button
                        onClick={() => handleDownload(job.id)}
                        className="btn-secondary text-sm inline-flex items-center space-x-1"
                      >
                        <Download className="w-3 h-3" />
                        <span>Download</span>
                      </button>
                      <button
                        onClick={() => handleRestore(job.id)}
                        disabled={!!restoreJobId}
                        className="btn-secondary text-sm inline-flex items-center space-x-1 disabled:opacity-50"
                      >
                        <Upload className="w-3 h-3" />
                        <span>Restore</span>
                      </button>
                    </>
                  )}
                  {(job.status === 'completed' || job.status === 'failed') && canDelete && (
                    <button
                      onClick={() => setDeleteConfirm({ isOpen: true, backupId: job.id })}
                      className="btn-secondary text-sm inline-flex items-center space-x-1 text-red-600 hover:text-red-700"
                    >
                      <Trash2 className="w-3 h-3" />
                      <span>Delete</span>
                    </button>
                  )}
                </div>
              </div>
            ))}
          </div>
        ) : (
          <div className="text-center py-8 text-gray-500">
            <p>No backups found</p>
          </div>
        )}
        {data.nextBackup && (
          <div className="mt-4 p-3 bg-blue-50 rounded-lg text-sm text-blue-700">
            <strong>Next backup:</strong> {formatDate(new Date(data.nextBackup), dateSettings)}
          </div>
        )}
      </div>

      {/* Restore History */}
      {restoreJobs.length > 0 && (
        <div>
          <h2 className="text-lg font-semibold text-gray-900 mb-4">Restore History</h2>
          <div className="space-y-2">
            {restoreJobs.map((job) => (
              <div key={job.id} className="flex items-center justify-between p-4 bg-gray-50 rounded-lg">
                <div className="flex items-center space-x-4">
                  <Upload className="w-5 h-5 text-gray-400" />
                  <div>
                    <div className="text-sm font-medium text-gray-900">
                      Restore from: {job.fileName || 'Unknown'}
                    </div>
                    <div className="text-xs text-gray-500">
                      {job.createdAt ? formatDate(new Date(job.createdAt), dateSettings) : 'Unknown'}
                    </div>
                    {job.errorMessage && (
                      <div className="text-xs text-red-600 mt-1">{job.errorMessage}</div>
                    )}
                  </div>
                  <span className={`inline-flex items-center space-x-1 px-2.5 py-0.5 rounded-full text-xs font-medium ${getStatusColor(job.status)}`}>
                    {getStatusIcon(job.status)}
                    <span>{getStatusLabel(job.status)}</span>
                  </span>
                </div>
              </div>
            ))}
          </div>
        </div>
      )}

      {/* In-progress restore indicator */}
      {restoreJobId && !showDryRunModal && (
        <div className="p-4 bg-blue-50 border border-blue-200 rounded-lg">
          <div className="flex items-center space-x-3">
            <Loader2 className="w-5 h-5 text-blue-600 animate-spin" />
            <div>
              <div className="text-sm font-medium text-blue-800">
                {restoreJob?.status === 'restoring' ? 'Restoring database...' : 'Running integrity check...'}
              </div>
              <div className="text-xs text-blue-600">Please wait, this may take a few minutes.</div>
            </div>
          </div>
        </div>
      )}

      {/* Save Button */}
      <div className="flex justify-end">
        {canUpdate && (
        <button
          onClick={handleSave}
          disabled={saving}
          className="btn-primary inline-flex items-center space-x-2 disabled:opacity-50"
        >
          <Save className="w-4 h-4" />
          <span>{saving ? 'Saving...' : 'Save Changes'}</span>
        </button>
        )}
      </div>

      {/* Delete Confirmation */}
      <ConfirmDialog
        isOpen={deleteConfirm.isOpen}
        onClose={() => setDeleteConfirm({ isOpen: false, backupId: null })}
        onConfirm={handleDelete}
        title="Delete Backup"
        message="Are you sure you want to delete this backup? The backup file will be permanently removed from disk. This cannot be undone."
        confirmText="Delete"
        cancelText="Cancel"
        variant="danger"
      />

      {/* Dry Run Results Modal */}
      {showDryRunModal && restoreJob?.dryRunResult && (
        <DryRunModal
          job={restoreJob}
          confirmText={confirmText}
          onConfirmTextChange={setConfirmText}
          onConfirm={handleConfirmRestore}
          onCancel={() => {
            setShowDryRunModal(false);
            setRestoreJobId(null);
            setRestoreJob(null);
            setConfirmText('');
          }}
          isConfirming={confirming}
        />
      )}
    </div>
  );
};

// ─── Dry Run Results Modal ──────────────────────────────────────

interface DryRunModalProps {
  job: BackupJob;
  confirmText: string;
  onConfirmTextChange: (text: string) => void;
  onConfirm: () => void;
  onCancel: () => void;
  isConfirming: boolean;
}

const DryRunModal = ({ job, confirmText, onConfirmTextChange, onConfirm, onCancel, isConfirming }: DryRunModalProps) => {
  const result = job.dryRunResult;
  if (!result) return null;

  const allChecksumsValid = result.allChecksumsValid;
  const schemaMatch = result.schemaVersion?.match;

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50">
      <div className="bg-white rounded-xl shadow-2xl max-w-2xl w-full mx-4 max-h-[80vh] overflow-y-auto">
        <div className="p-6">
          <h3 className="text-lg font-semibold text-gray-900 flex items-center space-x-2 mb-4">
            <AlertTriangle className="w-5 h-5 text-amber-500" />
            <span>Restore Verification Results</span>
          </h3>

          {/* Integrity Check */}
          <div className="space-y-4">
            <div className="p-3 bg-gray-50 rounded-lg">
              <h4 className="text-sm font-medium text-gray-700 mb-2">Integrity Check</h4>
              <div className="flex items-center space-x-2">
                {allChecksumsValid
                  ? <CheckCircle className="w-4 h-4 text-green-600" />
                  : <XCircle className="w-4 h-4 text-red-600" />
                }
                <span className={`text-sm ${allChecksumsValid ? 'text-green-700' : 'text-red-700'}`}>
                  {allChecksumsValid ? 'All checksums valid' : 'Checksum mismatch detected'}
                </span>
              </div>
            </div>

            {/* Schema Version */}
            <div className="p-3 bg-gray-50 rounded-lg">
              <h4 className="text-sm font-medium text-gray-700 mb-2">Schema Version</h4>
              <div className="flex items-center space-x-2">
                {schemaMatch
                  ? <CheckCircle className="w-4 h-4 text-green-600" />
                  : <AlertTriangle className="w-4 h-4 text-amber-500" />
                }
                <span className="text-sm text-gray-700">
                  Backup: <code className="bg-gray-200 px-1 rounded text-xs">{result.schemaVersion?.backup}</code>
                  {' | '}
                  Current: <code className="bg-gray-200 px-1 rounded text-xs">{result.schemaVersion?.current}</code>
                </span>
              </div>
            </div>

            {/* Table Counts */}
            <div className="p-3 bg-gray-50 rounded-lg">
              <h4 className="text-sm font-medium text-gray-700 mb-2">Table Row Counts (Backup vs Current)</h4>
              <div className="max-h-40 overflow-y-auto">
                <table className="w-full text-xs">
                  <thead>
                    <tr className="text-left text-gray-500">
                      <th className="pb-1">Table</th>
                      <th className="pb-1 text-right">Backup</th>
                      <th className="pb-1 text-right">Current</th>
                    </tr>
                  </thead>
                  <tbody>
                    {Object.keys(result.backupTableCounts || {}).sort().map(table => (
                      <tr key={table} className="border-t border-gray-100">
                        <td className="py-0.5 font-mono">{table}</td>
                        <td className="py-0.5 text-right">{result.backupTableCounts[table]?.toLocaleString() ?? '-'}</td>
                        <td className="py-0.5 text-right">{result.currentTableCounts?.[table]?.toLocaleString() ?? '-'}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </div>

            {/* Warnings */}
            {result.warnings?.length > 0 && (
              <div className="p-3 bg-amber-50 border border-amber-200 rounded-lg">
                <h4 className="text-sm font-medium text-amber-800 mb-1">Warnings</h4>
                <ul className="list-disc list-inside text-xs text-amber-700 space-y-1">
                  {result.warnings.map((w: string, i: number) => (
                    <li key={i}>{w}</li>
                  ))}
                </ul>
              </div>
            )}

            {/* Danger Warning */}
            <div className="p-4 bg-red-50 border border-red-200 rounded-lg">
              <div className="flex items-start space-x-3">
                <AlertTriangle className="w-5 h-5 text-red-600 mt-0.5 flex-shrink-0" />
                <div>
                  <h4 className="text-sm font-bold text-red-800">Warning: Data Loss</h4>
                  <p className="text-sm text-red-700 mt-1">
                    Restoring will <strong>replace ALL current data</strong> with the backup contents.
                    This action <strong>cannot be undone</strong>. All active sessions will be terminated.
                  </p>
                  <div className="mt-3">
                    <label className="block text-sm text-red-800 mb-1">
                      Type <strong>RESTORE</strong> to confirm:
                    </label>
                    <input
                      type="text"
                      value={confirmText}
                      onChange={(e) => onConfirmTextChange(e.target.value)}
                      className="input-base w-full border-red-300 focus:border-red-500 focus:ring-red-500"
                      placeholder="RESTORE"
                      autoFocus
                    />
                  </div>
                </div>
              </div>
            </div>
          </div>

          {/* Actions */}
          <div className="flex justify-end space-x-3 mt-6">
            <button
              onClick={onCancel}
              className="btn-secondary"
              disabled={isConfirming}
            >
              Cancel
            </button>
            <button
              onClick={onConfirm}
              disabled={confirmText !== 'RESTORE' || isConfirming}
              className="bg-red-600 hover:bg-red-700 text-white px-4 py-2 rounded-lg text-sm font-medium disabled:opacity-50 disabled:cursor-not-allowed inline-flex items-center space-x-2"
            >
              {isConfirming ? <Loader2 className="w-4 h-4 animate-spin" /> : <AlertTriangle className="w-4 h-4" />}
              <span>{isConfirming ? 'Restoring...' : 'Confirm Restore'}</span>
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};

// ─── Shared Form Components ─────────────────────────────────────

const FormField = ({
  label,
  value,
  onChange,
  type = 'text',
  options
}: {
  label: string;
  value: string;
  onChange: (e: React.ChangeEvent<HTMLInputElement | HTMLSelectElement>) => void;
  type?: 'text' | 'number' | 'select' | 'time';
  options?: string[];
}) => {
  if (type === 'select' && options) {
    return (
      <div>
        <label className="block text-sm font-medium text-gray-700 mb-2">{label}</label>
        <select value={value} onChange={onChange} className="input-base w-full">
          {options.map((opt) => (
            <option key={opt} value={opt}>{opt}</option>
          ))}
        </select>
      </div>
    );
  }

  return (
    <div>
      <label className="block text-sm font-medium text-gray-700 mb-2">{label}</label>
      <input
        type={type}
        value={value}
        onChange={onChange}
        className="input-base w-full"
      />
    </div>
  );
};

const ToggleField = ({
  label,
  checked,
  onChange
}: {
  label: string;
  checked: boolean;
  onChange: (checked: boolean) => void;
}) => (
  <div className="flex items-center justify-between">
    <label className="text-sm font-medium text-gray-700">{label}</label>
    <label className="relative inline-flex items-center cursor-pointer">
      <input
        type="checkbox"
        checked={checked}
        onChange={(e) => onChange(e.target.checked)}
        className="sr-only peer"
      />
      <div className="w-11 h-6 bg-gray-200 peer-focus:outline-none peer-focus:ring-4 peer-focus:ring-primary-100 rounded-full peer peer-checked:after:translate-x-full peer-checked:after:border-white after:content-[''] after:absolute after:top-[2px] after:left-[2px] after:bg-white after:border-gray-300 after:border after:rounded-full after:h-5 after:w-5 after:transition-all peer-checked:bg-primary-600"></div>
    </label>
  </div>
);

export default BackupRestoreTab;
